import React, { useEffect, useState, ChangeEvent, SyntheticEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import Title from '../../../components/Title';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { Type_Country } from '../../../models/type_country';
import { Banks } from '../../../models/banks';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const BancoEdit: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [typeCountry, setTypeCountry] = useState<Type_Country | null>(null);
  const [typeCountryList, setTypeCountryList] = useState<Type_Country[]>([]);
  const [isModified, setIsModified] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        showLoading();

        const [resBank, resCountries] = await Promise.all([
          axios.get(`banks/${id}`),
          axios.get(`typecountry`)
        ]);

        const bank: Banks = resBank.data.data;
        setName(bank.name);
        setTypeCountry(bank.type_country);
        setTypeCountryList(resCountries.data.data); // 👈 corregido aquí

        showAlert('Datos cargados correctamente', 'info');
      } catch (error) {
        console.error('Error al cargar datos:', error);
        showAlert('Error al obtener datos del banco o países', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [id]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setIsModified(true);
  };

  const handleTypeCountryChange = (e: SelectChangeEvent<number>) => {
    const selectedId = Number(e.target.value); // Asegura conversión a number
    const selectedCountry = typeCountryList.find(tc => tc.id === selectedId) || null;
    setTypeCountry(selectedCountry);
    setIsModified(true);
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!name.trim() || !typeCountry) {
      showAlert('El nombre del banco y país son obligatorios', 'warning');
      return;
    }

    try {
      showLoading();
      await axios.put(`banks`, {
        id,
        name,
        type_country_id: typeCountry.id
      });
      showAlert('Banco actualizado correctamente', 'success');
      navigate('/administrar/bancos');
    } catch (error) {
      console.error('Error al actualizar banco:', error);
      showAlert('Error al actualizar el banco', 'error');
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Editar Banco</Title>
        <Button variant="contained" startIcon={isMobile ? null : <AddIcon />} component={NavLink} to="/administrar/bancos/add">
          {isMobile ? <AddIcon /> : 'Agregar'}
        </Button>
      </Box>

      <Card>
        <Divider />
        <form onSubmit={handleSubmit}>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre del Banco"
                  value={name}
                  onChange={handleNameChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>País</InputLabel>
                  <Select
                    size="small"
                    value={typeCountry?.id || ''}
                    onChange={handleTypeCountryChange}
                    label="País"
                  >
                    {typeCountryList.map((tc) => (
                      <MenuItem key={tc.id} value={tc.id}>
                        {tc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <CardActions>
            <Button
              size='small'
              type="submit"
              variant="contained"
              disabled={!isModified}
            >
              Guardar
            </Button>
            <Button
              size='small'
              color="secondary"
              variant="outlined"
              onClick={() => navigate('/administrar/bancos')}
            >
              Cancelar
            </Button>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default BancoEdit;
